// search.ts — navbar live-search subsystem (template.js cleanup roadmap T2.1, 2026-06-14).
//
// Byte-faithful port of the navbar search-bar autocomplete + spyglass/spinner trailing UI + the
// search-term analytics beacon, carved out of template.js. Self-contained: the only external
// global it invokes at runtime is BASE_URL (core-helpers), present by the time any
// keystroke/submit/click fires.
//
// Public surface (window-attached at the bottom — runtime callers reach these as bare-identifier
// globals): liveSearchItems (PHP oninput on the desktop + mobile search inputs),
// ftLogSearchTerm (search.php Enter handler, typeof-guarded), installFtNavbarSearchTrailing
// (template.js DOMContentLoaded "DEFAULT RUNS" block), redirectInputItemId (PHP onchange).
//
// ftNavbarSearchTrailingSync / navbarSearchTrailingBusy + the two module-state vars stay
// module-local (grep confirmed zero external callers). The two top-level document listeners run
// at bundle-load (was template.js parse time) — they only REGISTER delegated handlers.
//
// Loaded AFTER template.js; the install call fires from template.js's DOMContentLoaded block,
// which runs after all footer bundles have loaded, so the window attachment is in time.

import { itemTypeBadgeInlineStyle } from '../shared/item-type-badge';

// ——————————————————————————————————————
// LIVE SEARCH — spyglass ↔ spinner while AJAX runs (avoid d-inline-flex + d-none together: both !important display)
// ——————————————————————————————————————
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
const searchAjaxGenByField: Record<'desktop' | 'mobile', number> = { desktop: 0, mobile: 0 };

interface NavbarSearchResult {
	id: string;
	label: string;
	type: string;
	typeLabel: string;
	url: string;
	iconId: string;
	enabled: boolean;
}

function navbarSearchRecord(value: unknown): Record<string, unknown> | null {
	return typeof value === 'object' && value !== null && !Array.isArray(value)
		? value as Record<string, unknown>
		: null;
}

function normalizeNavbarSearchPayload(value: unknown): NavbarSearchResult[] | null {
	const payload = navbarSearchRecord(value);
	if (!payload || payload.shape_v !== 1 || !Array.isArray(payload.results)) {
		return null;
	}
	const results: NavbarSearchResult[] = [];
	for (const raw of payload.results) {
		const row = navbarSearchRecord(raw);
		const icon = navbarSearchRecord(row?.icon);
		const action = navbarSearchRecord(row?.action);
		if (!row || typeof row.id !== 'string' || typeof row.label !== 'string'
			|| typeof row.type !== 'string' || typeof row.type_label !== 'string'
			|| typeof row.url !== 'string' || !icon || typeof icon.id !== 'string'
			|| !action || typeof action.enabled !== 'boolean') {
			return null;
		}
		results.push({
			id: row.id,
			label: row.label,
			type: row.type,
			typeLabel: row.type_label,
			url: row.url,
			iconId: icon.id,
			enabled: action.enabled,
		});
	}
	return results;
}

function renderNavbarSearchResults(box: HTMLElement, results: NavbarSearchResult[]): void {
	box.replaceChildren();
	if (results.length === 0) {
		const empty = document.createElement('div');
		empty.className = 'p-3 text-muted small text-center';
		empty.textContent = 'No results found.';
		box.append(empty);
		return;
	}
	const list = document.createElement('ul');
	list.className = 'item-list';
	for (const result of results) {
		const link = document.createElement('a');
		link.href = result.url;
		link.dataset.ftResultId = result.id;
		if (!result.enabled) {
			link.setAttribute('aria-disabled', 'true');
			link.addEventListener('click', (event) => event.preventDefault());
		}
		const li = document.createElement('li');
		const row = document.createElement('div');
		row.className = 'd-flex flex-row align-items-center gap-2';
		const label = document.createElement('div');
		label.className = 'flex-grow-1 min-w-0';
		label.textContent = result.label;
		const trailing = document.createElement('div');
		trailing.className = 'ms-auto d-flex align-items-center gap-2 flex-shrink-0';
		const badge = document.createElement('div');
		badge.className = 'badge';
		badge.style.cssText = itemTypeBadgeInlineStyle(result.type);
		badge.textContent = result.typeLabel;
		const icon = document.createElement('span');
		icon.className = 'flex-shrink-0';
		if (typeof window.iconMap === 'function') {
			icon.innerHTML = window.iconMap(result.iconId, 22, 22, false);
			// Arbitrary item UUIDs are not all present in the browser icon registry.
			// Keep a bespoke UUID icon when it resolves, otherwise mirror icon_map's
			// item-type fallback instead of leaving the help-circle placeholder.
			const resolvedSrc = icon.querySelector('img')?.getAttribute('src') || '';
			if (
				result.type !== result.iconId
				&& (resolvedSrc === '' || resolvedSrc.includes('help-circle-outline'))
			) {
				icon.innerHTML = window.iconMap(result.type, 22, 22, false);
			}
		}
		trailing.append(badge, icon);
		row.append(label, trailing);
		li.append(row);
		link.append(li);
		list.append(link);
	}
	box.append(list);
}

function navbarSearchFamilyMode(): 'php' | 'json' | 'compare' {
	const mode = new URLSearchParams(window.location.search).get('search_family');
	return mode === 'php' || mode === 'compare' ? mode : 'json';
}

async function fetchNavbarLegacyHtml(query: string): Promise<string> {
	const response = await fetch(BASE_URL + 'options/search/search_bar_items_inc.php', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ input_search_bar_items: query }),
	});
	if (!response.ok) {
		throw new Error(`Navbar PHP search failed (${response.status})`);
	}
	return response.text();
}

function ftNavbarSearchTrailingSync(which: string): void {
	const input = document.getElementById(
		which === 'mobile' ? 'input_search_bar_items_mobile' : 'input_search_bar_items',
	) as HTMLInputElement | null;
	const glyph = document.getElementById('search_glyph_' + which);
	const clearBtn = document.getElementById('search_clear_' + which);
	const loader = document.getElementById('search_loader_' + which);
	if (!input || !glyph || !clearBtn || !loader) {
		return;
	}
	const busy = !loader.classList.contains('d-none');
	if (busy) {
		glyph.classList.add('d-none');
		clearBtn.classList.add('d-none');
		return;
	}
	const hasText = (input.value || '').trim().length > 0;
	if (hasText) {
		glyph.classList.add('d-none');
		clearBtn.classList.remove('d-none');
	} else {
		clearBtn.classList.add('d-none');
		glyph.classList.remove('d-none');
	}
}

function installFtNavbarSearchTrailing(): void {
	['desktop', 'mobile'].forEach(function (which) {
		const input = document.getElementById(
			which === 'mobile' ? 'input_search_bar_items_mobile' : 'input_search_bar_items',
		) as HTMLInputElement | null;
		const clearBtn = document.getElementById('search_clear_' + which);
		if (!input || !clearBtn) {
			return;
		}
		clearBtn.addEventListener('click', function (ev) {
			ev.preventDefault();
			ev.stopPropagation();
			input.value = '';
			input.focus();
			const box =
				which === 'mobile'
					? document.getElementById('search_bar_suggestion_box_mobile')
					: document.getElementById('search_bar_suggestion_box');
			if (box) {
				box.style.display = 'none';
				box.innerHTML = '';
			}
			if (typeof liveSearchItems === 'function') {
				liveSearchItems(input);
			} else {
				navbarSearchTrailingBusy(which, false);
			}
		});
	});
	ftNavbarSearchTrailingSync('desktop');
	ftNavbarSearchTrailingSync('mobile');
}

function navbarSearchTrailingBusy(which: string, busy: boolean): void {
	const glyph = document.getElementById('search_glyph_' + which);
	const loader = document.getElementById('search_loader_' + which);
	const clearBtn = document.getElementById('search_clear_' + which);
	if (!glyph || !loader) {
		return;
	}
	if (busy) {
		glyph.classList.add('d-none');
		if (clearBtn) {
			clearBtn.classList.add('d-none');
		}
		loader.classList.remove('d-none');
	} else {
		loader.classList.add('d-none');
		ftNavbarSearchTrailingSync(which);
	}
}

// ---------------------------------------------------------------------------
// Search-term logging (item_searches). Logs ONLY on deliberate intent, never on
// keystrokes: (1) submitting a navbar search form (Enter / submit button), and
// (2) clicking an autocomplete suggestion (logs the term + the clicked id). The
// /search page Enter is logged from search.php's own handler via this same fn.
// Fire-and-forget via sendBeacon so it survives the ensuing navigation.
// ---------------------------------------------------------------------------
function ftLogSearchTerm(term: unknown, itemId?: unknown): void {
	try {
		const normalizedTerm = String(term ?? '').trim();
		if (normalizedTerm.length < 2) {
			return;
		}
		const payload = JSON.stringify({ term: normalizedTerm, item_id: itemId || null });
		const url = BASE_URL + 'options/search/log_search_term_inc.php';
		if (navigator.sendBeacon) {
			navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
		} else {
			fetch(url, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: payload,
				keepalive: true,
			});
		}
	} catch (e) {
		// Analytics must never break navigation.
	}
}

// Navbar search form submit (Enter or the hidden submit button) — desktop + mobile.
document.addEventListener('submit', function (e) {
	const form = e.target;
	if (!(form instanceof HTMLFormElement)) {
		return;
	}
	const input = form.querySelector<HTMLInputElement>('#input_search_bar_items, #input_search_bar_items_mobile');
	if (input) {
		ftLogSearchTerm(input.value);
	}
});

// Navbar autocomplete suggestion click — log the term we typed + the chosen id.
document.addEventListener('click', function (e) {
	const target = e.target;
	if (!(target instanceof Element)) {
		return;
	}
	const link = target.closest<HTMLAnchorElement>('#search_bar_suggestion_box a, #search_bar_suggestion_box_mobile a');
	if (!link) {
		return;
	}
	const isMobile = !!link.closest('#search_bar_suggestion_box_mobile');
	const input = document.getElementById(isMobile ? 'input_search_bar_items_mobile' : 'input_search_bar_items') as HTMLInputElement | null;
	ftLogSearchTerm(input ? input.value : '', link.getAttribute('data-ft-result-id') || null);
});

async function liveSearchItems(fromInput?: unknown): Promise<void> {
	const desktopEl = document.getElementById('input_search_bar_items') as HTMLInputElement | null;
	const mobileEl = document.getElementById('input_search_bar_items_mobile') as HTMLInputElement | null;
	const inputEl: HTMLInputElement | null =
		fromInput instanceof HTMLInputElement
			? fromInput
			: document.activeElement === mobileEl && mobileEl
				? mobileEl
				: document.activeElement === desktopEl && desktopEl
					? desktopEl
					: desktopEl || mobileEl;
	if (!inputEl) {
		return;
	}
	const which =
		inputEl.id === 'input_search_bar_items_mobile' ? 'mobile' : 'desktop';
	const box =
		which === 'mobile'
			? document.getElementById('search_bar_suggestion_box_mobile')
			: document.getElementById('search_bar_suggestion_box');
	const query = (inputEl.value || '').trim();
	ftNavbarSearchTrailingSync(which);
	if (query.length < 2) {
		if (box) {
			box.style.display = 'none';
			box.innerHTML = '';
		}
		navbarSearchTrailingBusy(which, false);
		return;
	}
	if (searchDebounceTimer !== null) clearTimeout(searchDebounceTimer);
	const querySnapshot = query;

	searchDebounceTimer = setTimeout(async () => {
		const inputFresh =
			which === 'mobile'
				? document.getElementById('input_search_bar_items_mobile') as HTMLInputElement | null
				: document.getElementById('input_search_bar_items') as HTMLInputElement | null;
		if (!inputFresh || (inputFresh.value || '').trim().length < 2) {
			navbarSearchTrailingBusy(which, false);
			return;
		}

		let boxLive =
			which === 'mobile'
				? document.getElementById('search_bar_suggestion_box_mobile')
				: document.getElementById('search_bar_suggestion_box');
		if (!boxLive) {
			return;
		}

		const genSnap = ++searchAjaxGenByField[which];
		navbarSearchTrailingBusy(which, true);
		boxLive.style.opacity = '0.6';
		boxLive.style.display = 'block';

		try {
			const mode = navbarSearchFamilyMode();
			let legacyHtml: string | null = null;
			let results: NavbarSearchResult[] | null = null;
			if (mode === 'php') {
				legacyHtml = await fetchNavbarLegacyHtml(querySnapshot);
			} else {
				try {
					const response = await fetch(BASE_URL + 'options/api/navbar_search_inc.php', {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({ query: querySnapshot }),
					});
					if (!response.ok) {
						throw new Error(`Navbar JSON search failed (${response.status})`);
					}
					results = normalizeNavbarSearchPayload(await response.json());
					if (results === null) {
						throw new Error('Navbar JSON search returned an invalid payload');
					}
					if (mode === 'compare') {
						void fetchNavbarLegacyHtml(querySnapshot).catch(() => undefined);
					}
				} catch (jsonError) {
					console.error('Navbar JSON search error; using PHP rollback:', jsonError);
					legacyHtml = await fetchNavbarLegacyHtml(querySnapshot);
				}
			}

			if (genSnap !== searchAjaxGenByField[which]) {
				return;
			}

			boxLive =
				which === 'mobile'
					? document.getElementById('search_bar_suggestion_box_mobile')
					: document.getElementById('search_bar_suggestion_box');
			if (!boxLive) {
				return;
			}

			if (results !== null) {
				renderNavbarSearchResults(boxLive, results);
			} else {
				boxLive.innerHTML = legacyHtml
					|| '<div class="p-3 text-muted small text-center">No results found.</div>';
			}
			boxLive.style.opacity = '1';
		} catch (err) {
			console.error('Search error:', err);
			boxLive =
				which === 'mobile'
					? document.getElementById('search_bar_suggestion_box_mobile')
					: document.getElementById('search_bar_suggestion_box');
			if (boxLive && genSnap === searchAjaxGenByField[which]) {
				boxLive.style.opacity = '1';
			}
		} finally {
			if (genSnap === searchAjaxGenByField[which]) {
				navbarSearchTrailingBusy(which, false);
			}
		}
	}, 50);
}

async function redirectInputItemId(input_id_or_element: unknown): Promise<void> {
    try {
        // Check if the input is an element or an element ID
        let inputElement: HTMLInputElement;
        if (typeof input_id_or_element === 'string') {
            const found = document.getElementById(input_id_or_element);
            if (!(found instanceof HTMLInputElement)) {
                throw new Error(`Element with id "${input_id_or_element}" not found`);
            }
            inputElement = found;
        } else if (input_id_or_element instanceof HTMLInputElement) {
            inputElement = input_id_or_element;
        } else {
            throw new Error("Provided input is neither a string nor an HTML element");
        }

        // Get the value of the input element
        var item_id = inputElement.value;
        if (!item_id) {
            throw new Error(`No item ID provided in input`);
        }

        // Redirect to the sheet
        window.location.href = BASE_URL + "item.php?id=" + encodeURIComponent(item_id);
    } catch (error) {
        console.error("Error in redirectInputItemId:", error);
    }
}

// ===========================================================================================
// Window attachment — preserve the exact public surface the originals exposed in template.js.
// liveSearchItems (PHP oninput), ftLogSearchTerm (search.php), installFtNavbarSearchTrailing
// (template.js DOMContentLoaded), redirectInputItemId (PHP onchange). The trailing-sync helpers
// + module state stay private.
// ===========================================================================================
declare global {
    interface Window {
        liveSearchItems: typeof liveSearchItems;
        ftLogSearchTerm: typeof ftLogSearchTerm;
		installFtNavbarSearchTrailing: typeof installFtNavbarSearchTrailing;
		redirectInputItemId: typeof redirectInputItemId;
		iconMap?: (id: string, width?: number, height?: number, title?: string | boolean) => string;
    }
}

Object.assign(window, {
    liveSearchItems,
    ftLogSearchTerm,
    installFtNavbarSearchTrailing,
    redirectInputItemId,
});

export { normalizeNavbarSearchPayload, renderNavbarSearchResults };
